import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { fetchSettings } from '../../../store/modules/adminrole/adminroleActions';
import useFormSettings from '../../../hooks/useFormSettings';
import CommonInput from '../../Common/CommonInput';

const AdminroleForm = ({ initialData, onSave, isLoading, onClose }) => {
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('adminrole', fetchSettings);
    const { permission_group } = settings || {};

    const [formData, setFormData] = useState({
        name: "",
        permissions: [],
        initialPermission: [],
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                name: initialData.name || {},
                permissions: permission_group || [],
                initialPermission: initialData.initialPermission || []
            });
        }
    }, [initialData, permission_group]);

    useEffect(() => {
        if(!initialData && permission_group) {
            setFormData(prevFormData => ({
                ...prevFormData,
                permissions: permission_group
            }));
        }
    }, [permission_group, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const handlePermissionChange = (selectedOption, e, permissions) => {
        const valuesToRemove = permissions.map(item => item.value);
        const filteredArray2 = formData.initialPermission.filter(value => !valuesToRemove.includes(value));

        if(selectedOption.value) {
            filteredArray2.push(selectedOption.value);
        }

        setFormData(prevFormData => ({
            ...prevFormData,
            initialPermission: filteredArray2
        }));
    }

    const selectValueMap = (viewPermissions, initialPermission) => {
        const revokedPermission = viewPermissions.find(permission => permission.value === false);

        for (const permission of viewPermissions) {
            if (initialPermission.includes(permission.value)) {
                return permission;
            }
        }

        return revokedPermission || null;
    };

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">


                <div className="row">
                    <div className="col-md-12">  
                        <CommonInput
                            name="name"
                            label="Role Name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter name"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                </div>


                <div className="row">
                    <div className="col-md-12 mb-3">
                        <div className="sub-head">Permissions</div>
                        {formData?.permissions && formData?.permissions?.grouped && formData?.permissions?.grouped?.length > 0 ? (
                            formData?.permissions.grouped.map((group, index) => (
                                <div key={index}>
                                    <div className="sub-head-font mt-4">{group.as}</div>
                                    {group.views.length > 0 ? (
                                        group.views.map((view, viewIndex) => (
                                            <div className="d-flex justify-content-between align-items-center" key={viewIndex}>
                                                <div className="text-muted mt-2">{view.as}</div>
                                                <div className="permission-select">
                                                    <Select
                                                        name={`${group.as}-${view.as}`}
                                                        value={selectValueMap(view.permissions, formData.initialPermission)}
                                                        classNamePrefix="react-select"
                                                        styles={{
                                                            control: (baseStyles, state) => ({
                                                                ...baseStyles,
                                                                border: (state.isSelected) ? 0 : 0,
                                                                borderColor: (state.isSelected) ? '#ccc' : '#ccc'
                                                            }),
                                                            indicatorSeparator: (baseStyles, state) => ({
                                                                ...baseStyles,
                                                                backgroundColor: 'inherit'
                                                            }),
                                                        }}
                                                        onChange={(a,b) => handlePermissionChange(a,b, view.permissions)}
                                                        options={view.permissions}
                                                        menuPlacement="top"
                                                    />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div>No views available</div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div>No permissions available</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default AdminroleForm; 